import React from 'react';
import { Link } from 'react-router-dom';

/* Shared Hale console design tokens + primitives.
 * White / charcoal / Hale-red identity, Chivo display + IBM Plex body —
 * deliberately distinct from the ToxIntel portal. */

export const T = {
  WHITE: '#FFFFFF', NEUTRAL: '#F7F7F8', INK: '#111214', INK2: '#1C1D21',
  SOFT: '#3F3F46', MUTED: '#71717A', LINE: '#E4E4E7',
  RED: '#E63946', RED_DEEP: '#C9192C', PINK: '#FF4E6A',
};
export const FONT = {
  DISPLAY: "'Chivo', system-ui, -apple-system, sans-serif",
  BODY: "'IBM Plex Sans', system-ui, -apple-system, sans-serif",
  MONO: "'IBM Plex Mono', ui-monospace, monospace",
};

const TONE = {
  draft:                         { label: 'Draft',                 c: '#6B7280', b: 'rgba(107,114,128,0.10)' },
  not_started:                   { label: 'Not started',           c: '#6B7280', b: 'rgba(107,114,128,0.10)' },
  submitted:                     { label: 'Submitted',             c: '#1D4ED8', b: 'rgba(29,78,216,0.10)' },
  under_review:                  { label: 'Under review',          c: '#92610A', b: 'rgba(212,160,23,0.12)' },
  information_requested:         { label: 'Information requested', c: '#92610A', b: 'rgba(212,160,23,0.12)' },
  conditional_approval:          { label: 'Conditional approval',  c: '#92610A', b: 'rgba(212,160,23,0.12)' },
  pending_review:                { label: 'In review',             c: '#92610A', b: 'rgba(212,160,23,0.12)' },
  pending_revision:              { label: 'Revisions requested',   c: '#92610A', b: 'rgba(212,160,23,0.12)' },
  approved:                      { label: 'Approved',              c: '#15803D', b: 'rgba(22,163,74,0.10)' },
  approved_with_recommendations: { label: 'Approved (with notes)', c: '#15803D', b: 'rgba(22,163,74,0.10)' },
  not_approved:                  { label: 'Not approved',          c: '#B91C1C', b: 'rgba(185,28,28,0.10)' },
  active:                        { label: 'Active',                c: '#15803D', b: 'rgba(22,163,74,0.10)' },
  complete:                      { label: 'Complete',              c: '#15803D', b: 'rgba(22,163,74,0.10)' },
};

export function StatusPill({ status, testid }) {
  if (!status) return null;
  const t = TONE[status] || { label: String(status).replace(/_/g, ' '), c: T.MUTED, b: 'rgba(17,18,20,0.06)' };
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-md text-[10px] uppercase tracking-[0.16em]"
          style={{ color: t.c, backgroundColor: t.b, fontFamily: FONT.MONO }} data-testid={testid}>
      {t.label}
    </span>
  );
}

export function Eyebrow({ children, color = T.RED }) {
  return <p className="text-[10px] uppercase tracking-[0.22em] mb-3" style={{ color, fontFamily: FONT.MONO }}>{children}</p>;
}

export function PageHeading({ title, sub, testid }) {
  return (
    <div className="mb-9">
      <h1 className="text-[clamp(1.7rem,3.2vw,2.4rem)] font-black tracking-[-0.02em] leading-[1.08]"
          style={{ fontFamily: FONT.DISPLAY, color: T.INK }} data-testid={testid}>{title}</h1>
      {sub && <p className="mt-3 text-[14px] max-w-2xl" style={{ color: T.MUTED }}>{sub}</p>}
    </div>
  );
}

export function ConsoleCard({ children, className = '', style = {}, testid, ...rest }) {
  return (
    <div className={`rounded-xl border p-6 ${className}`} style={{ borderColor: T.LINE, backgroundColor: T.WHITE, ...style }} data-testid={testid} {...rest}>
      {children}
    </div>
  );
}

export function InfoRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5 border-b last:border-0" style={{ borderColor: T.LINE }}>
      <span className="text-[11px] uppercase tracking-[0.14em]" style={{ color: T.MUTED, fontFamily: FONT.MONO }}>{label}</span>
      <span className="text-[14px] font-medium text-right" style={{ color: T.INK }}>{value || '—'}</span>
    </div>
  );
}

export function EmptyState({ icon: Icon, title, desc, ctaLabel, ctaTo, testid }) {
  return (
    <div className="rounded-xl border border-dashed p-10 text-center" style={{ borderColor: T.LINE, backgroundColor: T.NEUTRAL }} data-testid={testid}>
      {Icon && <Icon size={26} weight="duotone" style={{ color: T.MUTED }} className="mx-auto mb-3" />}
      <p className="text-[15px] font-bold" style={{ fontFamily: FONT.DISPLAY, color: T.INK }}>{title}</p>
      {desc && <p className="text-[13px] mt-2 max-w-md mx-auto" style={{ color: T.MUTED }}>{desc}</p>}
      {ctaLabel && ctaTo && (
        <Link to={ctaTo} className="inline-flex items-center gap-2 mt-5 px-5 py-2.5 rounded-lg text-[12px] font-semibold"
              style={{ backgroundColor: T.RED, color: T.WHITE }}>{ctaLabel}</Link>
      )}
    </div>
  );
}

export function Spinner({ label = 'Loading…' }) {
  return (
    <div className="rounded-xl border p-10 flex items-center gap-3" style={{ borderColor: T.LINE, backgroundColor: T.WHITE }}>
      <span className="w-5 h-5 border-2 rounded-full animate-spin" style={{ borderColor: T.RED, borderTopColor: 'transparent' }} />
      <p className="text-[13px]" style={{ color: T.MUTED }}>{label}</p>
    </div>
  );
}
